import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Icon,
  Input,
  Stack,
  Text,
} from '@eluve/components';

const challengerCodeFormSchema = z.object({
  challengerCode: z
    .string()
    .min(6, 'Security code must be 6 digits')
    .max(6, 'Security code must be 6 digits'),
});

export type ChallengerCodeFormData = z.infer<typeof challengerCodeFormSchema>;

// Custom OTP Input Component
interface OTPInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const OTPInput: React.FC<OTPInputProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''));

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Update digits when value changes (for form control)
  useEffect(() => {
    if (value) {
      const newDigits = value.split('').slice(0, 6);
      // Pad with empty strings to ensure we always have 6 elements
      while (newDigits.length < 6) {
        newDigits.push('');
      }
      setDigits(newDigits);
    } else {
      // If value is empty, reset to 6 empty strings
      setDigits(Array(6).fill(''));
    }
  }, [value]);

  const handleInputChange = (index: number, inputValue: string) => {
    const newValue = inputValue.replace(/[^0-9]/g, '');

    if (newValue.length <= 1) {
      const newDigits = [...digits];
      newDigits[index] = newValue;
      setDigits(newDigits);
      onChange(newDigits.join(''));

      // Move to next input if value entered
      if (newValue && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      // Move to previous input and clear it
      const newDigits = [...digits];
      newDigits[index - 1] = '';
      setDigits(newDigits);
      onChange(newDigits.join(''));
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData('text')
      .replace(/[^0-9]/g, '')
      .slice(0, 6);

    if (pastedData) {
      const newDigits = pastedData.split('').slice(0, 6);
      while (newDigits.length < 6) {
        newDigits.push('');
      }
      setDigits(newDigits);
      onChange(newDigits.join(''));

      // Focus the next empty input or the last input
      const nextEmptyIndex = newDigits.findIndex((digit) => !digit);
      const focusIndex =
        nextEmptyIndex === -1 ? 5 : Math.min(nextEmptyIndex, 5);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  return (
    <Stack gap={2} direction="horizontal" justify="between">
      {digits.map((digit, index) => (
        <Input
          key={index}
          ref={(el: HTMLInputElement | null) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={digit}
          onChange={(e) => handleInputChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className="size-16 border text-center font-mono text-lg"
        />
      ))}
    </Stack>
  );
};

export interface ChallengerCodeFormProps {
  email: string;
  onSubmit: (data: ChallengerCodeFormData) => Promise<void>;
  onRequestNewCode: (email: string) => Promise<void>;
  isSubmitting?: boolean;
  isRequestingNewCode?: boolean;
  error?: string | null;
}

export const ChallengerCodeForm: React.FC<ChallengerCodeFormProps> = ({
  email,
  onSubmit,
  onRequestNewCode,
  isSubmitting = false,
  isRequestingNewCode = false,
  error = null,
}) => {
  const form = useForm<ChallengerCodeFormData>({
    resolver: zodResolver(challengerCodeFormSchema),
    defaultValues: {
      challengerCode: '',
    },
  });

  const handleSubmit = async (data: ChallengerCodeFormData) => {
    await onSubmit(data);
  };

  const handleRequestNewCode = async () => {
    await onRequestNewCode(email);
    // Clear the current code input
    form.setValue('challengerCode', '');
  };

  return (
    <Stack gap={2}>
      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <Icon name="AlertCircle" size="xl" className="size-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Text size="md">
        Check <span className="font-semibold">{email}</span> for a login email.
        You can click the magic link or enter the 6-digit code below.
      </Text>

      {/* Form */}
      <Form {...form}>
        <form className="my-4 w-full space-y-5">
          {/* Challenger Code Field */}
          <FormField
            control={form.control}
            name="challengerCode"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <OTPInput
                    value={field.value}
                    onChange={field.onChange}
                    disabled={isSubmitting}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Submit Button */}
          <Button
            onClick={form.handleSubmit(handleSubmit)}
            disabled={isSubmitting}
            fullWidth
          >
            {isSubmitting ? 'Verifying...' : 'Submit Code'}
          </Button>
          <Stack justify="center" gap={2} direction="horizontal">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRequestNewCode}
              disabled={isRequestingNewCode || isSubmitting}
            >
              {isRequestingNewCode ? 'Resending...' : 'Resend Code'}
            </Button>
          </Stack>
        </form>
      </Form>
    </Stack>
  );
};
